import { ArrowRightLeft, CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import type {
  MissingTargetView,
  ProjectVersionApprovalPreview,
  TransferCollisionView,
} from '@obraplan/core/application/contracts/structure';
import type { Project } from '@obraplan/core/domain/structure/project';
import { KEEP_IN_APPROVED } from '@obraplan/core/domain/structure/operation-transfer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * "Aprovar versão" com a operação (Fase 36 — RN-465 a RN-469).
 *
 * A versão vira a obra aprovada, e toda a operação da aprovada de hoje vem junto: funcionários
 * e equipes, cronograma, planejamento, RDO, peças e cômodos executados, qualidade e suprimento
 * (decisão do Gilvan, 20/10/2026). A prévia diz o que vai. O que não acha lugar na versão — a
 * frente, o pavimento, o cômodo ou a peça que ela não tem — pede um destino, ou fica na antiga;
 * o que cai no mesmo lugar de outra pede qual vale. Cada escolha refaz a prévia: um destino
 * escolhido pode criar um mesmo lugar novo.
 */

interface Escolhas {
  readonly missing: Readonly<Record<string, string>>;
  readonly collisions: Readonly<Record<string, string>>;
}

const NENHUMA: Escolhas = { missing: {}, collisions: {} };

const quantas = (linhas: readonly { readonly label: string; readonly count: number }[]) =>
  linhas.map((linha) => `${linha.label.toLowerCase()} (${String(linha.count)})`).join(', ');

export function ApproveVersionDialog({
  version,
  onClose,
  onApproved,
}: {
  /** A versão a aprovar; ausente, o diálogo fica fechado. */
  version: Project | undefined;
  onClose: () => void;
  onApproved: (approved: Project) => void;
}) {
  const container = useContainer();
  const { session } = useSessionState();
  const [escolhas, setEscolhas] = useState<Escolhas>(NENHUMA);

  // Cada versão aberta recomeça sem escolha nenhuma.
  useEffect(() => {
    setEscolhas(NENHUMA);
  }, [version?.id]);

  const comEscolhas = {
    ...(Object.keys(escolhas.missing).length === 0 ? {} : { missing: escolhas.missing }),
    ...(Object.keys(escolhas.collisions).length === 0 ? {} : { collisions: escolhas.collisions }),
  };
  const lida = useLive(
    () =>
      version === undefined || session === undefined
        ? Promise.resolve(undefined)
        : container.previewProjectVersionApproval.execute(
            { projectId: version.id, choices: comEscolhas },
            session,
          ),
    [version?.id, session?.userId, JSON.stringify(escolhas)],
  );
  const previa: ProjectVersionApprovalPreview | undefined =
    lida !== undefined && lida.ok ? lida.value : undefined;
  const erroDaPrevia = lida !== undefined && !lida.ok ? lida.error.message : undefined;

  const [aprovar, aprovando] = useCommand(
    () =>
      container.approveProjectVersion.execute(
        { projectId: version!.id, choices: comEscolhas },
        session!,
      ),
    {
      successMessage: (approved) => `${approved.name} é a obra aprovada, com a operação de antes.`,
      onSuccess: (approved) => onApproved(approved),
    },
  );

  const escolherDestino = (chave: string, destino: string) =>
    setEscolhas((atual) => {
      const missing = { ...atual.missing };
      if (destino === '') delete missing[chave];
      else missing[chave] = destino;
      return { ...atual, missing };
    });
  const escolherVencedora = (chave: string, linha: string) =>
    setEscolhas((atual) => ({ ...atual, collisions: { ...atual.collisions, [chave]: linha } }));

  return (
    <Dialog
      open={version !== undefined}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Aprovar {version?.name ?? ''}?</DialogTitle>
          <DialogDescription>
            Esta versão vira a obra aprovada
            {previa === undefined ? '' : `, e a operação de ${previa.approved.name} vem junto`}:
            funcionários e equipes, cronograma, planejamento, RDO, execução e qualidade. A obra
            aprovada hoje vira uma versão, só com o orçamento.
          </DialogDescription>
        </DialogHeader>

        {erroDaPrevia !== undefined && (
          <p role="alert" className="text-sm text-(--color-state-danger)">
            {erroDaPrevia}
          </p>
        )}
        {previa === undefined && erroDaPrevia === undefined && (
          <p className="text-sm text-(--color-content-muted)">Lendo o que vai junto…</p>
        )}

        {previa !== undefined && (
          <div className="flex flex-col gap-5">
            <section aria-label="O que vai" className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold">O que vai para {previa.version.name}</h3>
              {previa.moves.length === 0 ? (
                <p className="text-sm text-(--color-content-muted)">
                  A obra aprovada não tem operação: vai só a aprovação.
                </p>
              ) : (
                <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {previa.moves.map((linha) => (
                    <li key={linha.table}>
                      {linha.label} <span className="font-medium tabular-nums">{linha.count}</span>
                    </li>
                  ))}
                </ul>
              )}
              {previa.keeps.length > 0 && (
                <p className="text-sm text-(--color-content-muted)">
                  Fica em {previa.approved.name}: {quantas(previa.keeps)}.
                </p>
              )}
            </section>

            {previa.missing.length > 0 && (
              <section aria-label="Sem lugar na versão" className="flex flex-col gap-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <CircleAlert className="size-4 text-(--color-state-warn)" aria-hidden="true" />
                  Sem lugar na versão · {previa.missing.length}
                </h3>
                {previa.missing.map((alvo) => (
                  <SemLugar
                    key={alvo.key}
                    alvo={alvo}
                    escolha={escolhas.missing[alvo.key] ?? ''}
                    aprovada={previa.approved.name}
                    onEscolher={(destino) => escolherDestino(alvo.key, destino)}
                  />
                ))}
              </section>
            )}

            {previa.collisions.length > 0 && (
              <section aria-label="No mesmo lugar" className="flex flex-col gap-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <ArrowRightLeft className="size-4 text-(--color-state-warn)" aria-hidden="true" />
                  No mesmo lugar · {previa.collisions.length}
                </h3>
                {previa.collisions.map((colisao) => (
                  <MesmoLugar
                    key={colisao.key}
                    colisao={colisao}
                    escolha={escolhas.collisions[colisao.key]}
                    aprovada={previa.approved.name}
                    onEscolher={(linha) => escolherVencedora(colisao.key, linha)}
                  />
                ))}
              </section>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={previa === undefined || !previa.ready || aprovando.isPending}
            onClick={() => void aprovar()}
          >
            Aprovar versão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SemLugar({
  alvo,
  escolha,
  aprovada,
  onEscolher,
}: {
  alvo: MissingTargetView;
  escolha: string;
  aprovada: string;
  onEscolher: (destino: string) => void;
}) {
  const id = `destino-${alvo.key}`;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3 text-sm">
      <p>
        <span className="text-(--color-content-muted)">{alvo.kindLabel}:</span>{' '}
        <span className="font-medium">{alvo.label}</span>
        <span className="block text-xs text-(--color-content-muted)">
          A versão não tem. Apontam para ela: {quantas(alvo.rows)}.
        </span>
      </p>
      <label htmlFor={id} className="text-xs font-medium">
        Para onde vai
      </label>
      <select
        id={id}
        value={escolha}
        onChange={(evento) => onEscolher(evento.target.value)}
        className="h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9"
      >
        <option value="">Escolha…</option>
        {alvo.canKeep && <option value={KEEP_IN_APPROVED}>Deixar em {aprovada}</option>}
        {alvo.options.map((opcao) => (
          <option key={opcao.id} value={opcao.id}>
            {opcao.label}
          </option>
        ))}
      </select>
      {!alvo.canKeep && (
        <p className="text-xs text-(--color-content-muted)">
          Tem alocação de quem vai para a versão: precisa de um destino nela.
        </p>
      )}
    </div>
  );
}

function MesmoLugar({
  colisao,
  escolha,
  aprovada,
  onEscolher,
}: {
  colisao: TransferCollisionView;
  escolha: string | undefined;
  aprovada: string;
  onEscolher: (linha: string) => void;
}) {
  const consequencia = (ifLoses: TransferCollisionView['candidates'][number]['ifLoses']) =>
    ifLoses === 'KEEP'
      ? `se não valer, fica em ${aprovada}`
      : ifLoses === 'DISCARD'
        ? 'se não valer, sai'
        : 'não pode sair';
  return (
    <fieldset className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3 text-sm">
      <legend className="px-1 text-xs font-medium">{colisao.tableLabel}: qual vale?</legend>
      {colisao.candidates.map((candidata) => (
        <label key={candidata.id} className="flex items-start gap-2">
          <input
            type="radio"
            name={colisao.key}
            value={candidata.id}
            checked={escolha === candidata.id}
            onChange={() => onEscolher(candidata.id)}
            className="mt-1"
          />
          <span>
            {candidata.label}
            <span className="block text-xs text-(--color-content-muted)">
              {candidata.from === 'APPROVED' ? `De ${aprovada}` : 'Da versão'} ·{' '}
              {consequencia(candidata.ifLoses)}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}
